export function getCSSVariable(name: string, defaultValue: number): number {
  if (typeof window === 'undefined') return defaultValue;
  
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  
  if (!value) return defaultValue;
  
  if (value.endsWith('px')) {
    return parseFloat(value);
  }
  
  if (value.endsWith('dvh') || value.endsWith('vh')) {
    const percentage = parseFloat(value);
    return (percentage / 100) * window.innerHeight;
  }
  
  if (value.endsWith('dvw') || value.endsWith('vw')) {
    const percentage = parseFloat(value);
    return (percentage / 100) * window.innerWidth;
  }
  
  if (value.endsWith('%')) {
    const percentage = parseFloat(value);
    return percentage;
  }
  
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Get responsive floating window size from CSS variables.
 * Automatically adapts to viewport width and DPI scaling.
 * 
 * Uses media query breakpoints:
 * - Default (1920px+): 400x550px (100% scaling)
 * - 1600px and below: 350x480px (125% scaling)
 * - 1280px and below: 300x400px (150% scaling)
 * - Mobile (768px and below): full screen
 */
export function getResponsiveFloatingWindowSize(): { width: number; height: number } {
  return {
    width: getCSSVariable('--devxgpt-floating-width', 400),
    height: getCSSVariable('--devxgpt-floating-height', 600),
  };
}

/**
 * Calculate bottom-right position for the floating window.
 * Positions the window in the bottom-right corner with padding.
 */
export function getResponsiveFloatingWindowPosition(): { x: number; y: number } {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  const size = getResponsiveFloatingWindowSize();
  const padding = 16; // Default padding from edges

  return {
    x: window.innerWidth - size.width - padding,
    y: window.innerHeight - size.height - padding,
  };
}

